import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowRight } from "lucide-react";
import { gsap, ScrollTrigger, lineReveal, revealUp, counterTo } from "../lib/gsap";
import HeroCarousel from "../components/site/HeroCarousel";

const PORTFOLIO = [
  {
    key: "attacq",
    name: "Attacq Ltd",
    sector: "Real Estate REIT",
    note: "Master developer of Waterfall City, Gauteng — portfolio over R28bn.",
    logo: "https://customer-assets.emergentagent.com/job_motion-web-studio-1/artifacts/pis5n2qi_958266_edfbc99690e1460898c6abe764d4f55e~mv2.png.avif",
  },
  {
    key: "mtn",
    name: "MTN Group",
    sector: "Telecom · Fintech",
    note: "Africa's leading telecom operator pivoting to a digital ecosystem.",
    logo: "https://customer-assets.emergentagent.com/job_motion-web-studio-1/artifacts/7y23lxqb_channels4_profile.jpg",
  },
  {
    key: "valterra",
    name: "Valterra Platinum",
    sector: "Mining · PGMs",
    note: "World's leading primary producer of Platinum Group Metals.",
    logo: "https://customer-assets.emergentagent.com/job_motion-web-studio-1/artifacts/yk644i7d_Valterra-Platinum-Logo-1024x585.png.webp",
  },
];

const SECTORS = ["Logistics", "Property", "Telecom · Fintech", "Financial Services", "Mining · PGMs", "Infrastructure Development", "Renewable Energy"];

export default function Home() {
  const heroRef = useRef(null);
  const heroImgRef = useRef(null);
  const headlineRef = useRef(null);
  const statsRef = useRef(null);
  const portfolioRef = useRef(null);
  const philosophyRef = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      // Hero line reveal
      if (headlineRef.current) lineReveal(headlineRef.current, 0.2);

      // Hero reveal-up elements (tag, paragraph, buttons)
      if (heroRef.current) {
        const heroRevealEls = heroRef.current.querySelectorAll(".reveal-up");
        if (heroRevealEls.length) {
          gsap.to(heroRevealEls, {
            y: 0,
            opacity: 1,
            duration: 1.1,
            ease: "power3.out",
            stagger: 0.1,
            delay: 0.4,
          });
        }
      }

      // Hero parallax
      if (heroImgRef.current) {
        gsap.to(heroImgRef.current, {
          yPercent: 18,
          ease: "none",
          scrollTrigger: {
            trigger: heroRef.current,
            start: "top top",
            end: "bottom top",
            scrub: true,
          },
        });
      }

      // Stats counters
      if (statsRef.current) {
        statsRef.current.querySelectorAll("[data-counter]").forEach((el) => {
          counterTo(el, Number(el.dataset.counter || 0), 2.2);
        });
        revealUp(statsRef.current);
      }

      // Portfolio reveal
      if (portfolioRef.current) revealUp(portfolioRef.current);
      if (philosophyRef.current) revealUp(philosophyRef.current);

      // Generic
      gsap.utils.toArray(".js-reveal").forEach((el) => {
        gsap.fromTo(
          el,
          { y: 60, opacity: 0 },
          {
            y: 0,
            opacity: 1,
            duration: 1.1,
            ease: "power3.out",
            scrollTrigger: { trigger: el, start: "top 85%" },
          }
        );
      });
    });

    return () => {
      ctx.revert();
      ScrollTrigger.getAll().forEach((t) => t.kill());
    };
  }, []);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section ref={heroRef} className="relative min-h-screen flex items-end overflow-hidden grain">
        <div ref={heroImgRef} className="absolute inset-0 z-0">
          <img
            src="https://customer-assets.emergentagent.com/job_motion-web-studio-1/artifacts/pv757sje_AdobeStock_113517316.jpg"
            alt="Johannesburg skyline"
            className="w-full h-[120%] object-cover"
            style={{ filter: "saturate(1.05) brightness(0.95) contrast(1.05)", objectPosition: "center" }}
          />
          <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(5,9,20,0.25) 0%, rgba(5,9,20,0.35) 45%, rgba(5,9,20,0.78) 88%, var(--bg) 100%)" }} />
          <div className="absolute inset-0" style={{ background: "radial-gradient(ellipse at 28% 60%, rgba(5,9,20,0.45) 0%, rgba(5,9,20,0.15) 55%, rgba(5,9,20,0) 80%)" }} />
        </div>

        {/* Decorative design lines */}
        <svg
          className="absolute inset-0 w-full h-full pointer-events-none z-[1]"
          viewBox="0 0 1600 900"
          preserveAspectRatio="xMidYMid slice"
          aria-hidden="true"
        >
          <defs>
            <pattern id="hero-grid" width="80" height="80" patternUnits="userSpaceOnUse">
              <path d="M 80 0 L 0 0 0 80" fill="none" stroke="rgba(253,251,247,0.06)" strokeWidth="0.5" />
            </pattern>
            <radialGradient id="hero-glow" cx="78%" cy="58%" r="45%">
              <stop offset="0%" stopColor="rgba(200,16,46,0.22)" />
              <stop offset="60%" stopColor="rgba(200,16,46,0.05)" />
              <stop offset="100%" stopColor="rgba(200,16,46,0)" />
            </radialGradient>
            <linearGradient id="hero-line" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="rgba(200,16,46,0)" />
              <stop offset="50%" stopColor="rgba(200,16,46,0.55)" />
              <stop offset="100%" stopColor="rgba(200,16,46,0)" />
            </linearGradient>
          </defs>

          {/* Subtle grid */}
          <rect width="100%" height="100%" fill="url(#hero-grid)" />

          {/* Soft red glow */}
          <rect width="100%" height="100%" fill="url(#hero-glow)" />

          {/* Large concentric arcs (right) — globe / continent reference */}
          <g transform="translate(1280 480)" fill="none" stroke="rgba(253,251,247,0.10)" strokeWidth="0.8">
            <circle r="320" />
            <circle r="240" />
            <circle r="160" />
            <circle r="80" />
            <circle r="320" stroke="rgba(200,16,46,0.35)" strokeDasharray="2 6" />
          </g>

          {/* Diagonal accent lines */}
          <g stroke="rgba(253,251,247,0.08)" strokeWidth="0.7">
            <line x1="0" y1="180" x2="1600" y2="60" />
            <line x1="0" y1="780" x2="1600" y2="600" />
          </g>

          {/* Glowing horizontal red rule */}
          <line x1="0" y1="450" x2="1600" y2="450" stroke="url(#hero-line)" strokeWidth="1" />

          {/* Coordinate corner ticks (top-left) */}
          <g stroke="rgba(200,16,46,0.6)" strokeWidth="1" fill="none">
            <path d="M 120 120 L 120 160 M 120 120 L 160 120" />
          </g>
          <g stroke="rgba(200,16,46,0.6)" strokeWidth="1" fill="none">
            <path d="M 1480 780 L 1480 740 M 1480 780 L 1440 780" />
          </g>

          {/* Latitude/longitude label */}
          <text x="120" y="100" fill="rgba(253,251,247,0.35)" fontSize="10" letterSpacing="3" fontFamily="Manrope, sans-serif">
            25°44′S · 28°16′E — PRETORIA
          </text>
          <text x="1480" y="800" textAnchor="end" fill="rgba(253,251,247,0.35)" fontSize="10" letterSpacing="3" fontFamily="Manrope, sans-serif">
            EST · GICP — INVESTING IN PARTNERSHIPS
          </text>

          {/* Small data tick marks bottom-left */}
          <g stroke="rgba(253,251,247,0.18)" strokeWidth="0.7">
            <line x1="120" y1="820" x2="320" y2="820" />
            <line x1="120" y1="820" x2="120" y2="800" />
            <line x1="170" y1="820" x2="170" y2="810" />
            <line x1="220" y1="820" x2="220" y2="810" />
            <line x1="270" y1="820" x2="270" y2="810" />
            <line x1="320" y1="820" x2="320" y2="800" />
          </g>
        </svg>

        <div className="container-x pb-24 md:pb-32 pt-44 md:pt-52 relative z-10">
          <div className="max-w-5xl">
            <div className="tag mb-8 reveal-up" style={{ color: "#FDFBF7", borderColor: "rgba(253,251,247,0.45)" }}>
              <span className="gold-divider" style={{ background: "#FDFBF7" }} /> Est. Pretoria · Africa-wide
            </div>
            <h1 ref={headlineRef} className="font-serif text-white tracking-tighter leading-[0.92] text-5xl sm:text-6xl md:text-7xl lg:text-[7.5rem]">
              <span className="split-line"><span>Capital to fuel</span></span>
              <span className="split-line"><span><em className="italic font-light" style={{ color: "var(--gicp-gold)" }}>African</em> growth.</span></span>
            </h1>
            <p className="mt-10 max-w-2xl text-base md:text-lg leading-relaxed reveal-up" style={{ color: "#D8DCE6" }}>
              Global Investment Capital Partners is an institutional investment holding and corporate finance advisory firm.
              We deploy intelligent capital and lead execution-driven advisory mandates across the continent.
            </p>
            <div className="mt-12 flex flex-wrap gap-4 reveal-up">
              <Link to="/services" className="btn-gold" data-testid="hero-cta">
                Explore services <ArrowUpRight size={16} />
              </Link>
              <Link to="/about" className="btn-ghost" data-testid="hero-secondary" style={{ color: "white", borderColor: "rgba(255,255,255,0.25)" }}>
                Our philosophy
              </Link>
            </div>
          </div>
        </div>

        {/* Scroll indicator */}
        <div
          className="absolute bottom-16 md:bottom-20 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-3"
          style={{ color: "#FDFBF7" }}
        >
          <span className="text-[10px] tracking-[0.45em] uppercase">Scroll</span>
          <span className="block w-px h-10 animate-pulse" style={{ background: "#FDFBF7" }} />
        </div>
      </section>

      {/* SECTOR MARQUEE */}
      <section className="py-20 md:py-28" style={{ borderTop: "1px solid var(--border-subtle)", borderBottom: "1px solid var(--border-subtle)" }}>
        <div className="marquee">
          <div className="marquee-inner py-2">
            {[...SECTORS, ...SECTORS, ...SECTORS].map((s, i) => (
              <span key={i} className="font-serif italic text-5xl md:text-7xl tracking-tight leading-[1.25]" style={{ color: i % 2 === 0 ? "var(--fg)" : "var(--gicp-gold)", opacity: 0.85 }}>
                {s} <span style={{ color: "var(--gicp-gold)" }}>·</span>
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* IMAGE INTERLUDE */}
      <section className="relative h-[60vh] md:h-[75vh] overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=1800&q=80&auto=format&fit=crop"
          alt="Skyline towers"
          className="absolute inset-0 w-full h-full object-cover js-parallax"
          style={{ filter: "saturate(0.85) brightness(0.7)" }}
        />
        <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, var(--bg) 0%, transparent 25%, transparent 75%, var(--bg) 100%)" }} />
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="container-x text-center js-reveal">
            <span className="overline" style={{ color: "#FDFBF7" }}>— Anchored in Africa</span>
            <p className="font-serif italic text-3xl md:text-6xl mt-6 max-w-4xl mx-auto leading-[1.1]" style={{ color: "#FDFBF7" }}>
              &ldquo;The continent presents exceptional opportunities — success requires more than capital.&rdquo;
            </p>
          </div>
        </div>
      </section>

      {/* PORTFOLIO CAROUSEL */}
      <HeroCarousel />

      {/* STATS */}
      <section ref={statsRef} className="section" data-testid="stats-ribbon">
        <div className="container-x grid grid-cols-2 md:grid-cols-4 gap-12 md:gap-8">
          {[
            { v: 5, suffix: "+", label: "Sector mandates" },
            { v: 28, suffix: "bn", label: "Anchored portfolio (R)" },
            { v: 328, suffix: "MW+", label: "Renewable pipeline" },
            { v: 50, suffix: "M+", label: "USD syndicated capacity" },
          ].map((s, i) => (
            <div key={i} className="reveal-up">
              <div className="font-serif text-5xl md:text-7xl tabular tracking-tight" style={{ color: "var(--gicp-gold)" }}>
                <span data-counter={s.v}>0</span>
                <span className="text-2xl md:text-3xl align-top ml-1">{s.suffix}</span>
              </div>
              <div className="mt-3 text-xs tracking-[0.22em] uppercase" style={{ color: "var(--fg-soft)" }}>{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* PHILOSOPHY */}
      <section ref={philosophyRef} className="section" style={{ borderTop: "1px solid var(--border-subtle)" }}>
        <div className="container-x grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-16">
          <div className="md:col-span-5 reveal-up">
            <span className="overline">— Philosophy</span>
            <h2 className="font-serif text-4xl md:text-6xl mt-6 leading-[0.95] tracking-tight">
              We invest <em className="italic" style={{ color: "var(--gicp-gold)" }}>alongside</em> our partners.
            </h2>
          </div>
          <div className="md:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-px" style={{ background: "var(--border-subtle)" }}>
            {[
              { t: "Significant Minority", d: "Active stewardship without operational burden — partnering with sector-leading management." },
              { t: "Means of Production", d: "Tangible, productive capacity: logistics, telecoms, infrastructure, mining assets." },
              { t: "Risk-Adjusted Returns", d: "Rigorous, data-driven diligence. Predictable cash flows. Strong barriers to entry." },
              { t: "ESG Integration", d: "Responsibility woven into investment decisions — financial and moral imperative." },
            ].map((p, i) => (
              <div key={i} className="p-8 md:p-10 reveal-up" style={{ background: "var(--bg)" }}>
                <div className="text-xs tracking-[0.25em] uppercase" style={{ color: "var(--gicp-gold)" }}>0{i + 1}</div>
                <h3 className="mt-4 font-serif text-2xl md:text-3xl">{p.t}</h3>
                <p className="mt-4 text-sm leading-relaxed" style={{ color: "var(--fg-soft)" }}>{p.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PORTFOLIO BENTO */}
      <section ref={portfolioRef} className="section" style={{ background: "var(--bg-soft)" }}>
        <div className="container-x">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-8 mb-16">
            <div className="reveal-up">
              <span className="overline">— Portfolio Highlights</span>
              <h2 className="font-serif text-4xl md:text-6xl mt-4 leading-[0.95] tracking-tight max-w-2xl">
                Anchored in continent-defining<br/><em className="italic" style={{ color: "var(--gicp-gold)" }}>assets.</em>
              </h2>
            </div>
            <Link to="/about" className="btn-ghost reveal-up self-start md:self-auto" data-testid="portfolio-cta">
              See thesis <ArrowRight size={16} />
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-12">
            {PORTFOLIO.map((p) => (
              <article
                key={p.key}
                data-testid={`portfolio-card-${p.key}`}
                className="group reveal-up flex flex-col items-center text-center"
              >
                <div
                  className="relative w-48 h-48 md:w-56 md:h-56 rounded-full overflow-hidden flex items-center justify-center p-8 md:p-10 transition-transform duration-700 group-hover:-translate-y-1"
                  style={{ background: "var(--gicp-bone)", border: "1px solid var(--border-subtle)" }}
                >
                  <img
                    src={p.logo}
                    alt={`${p.name} logo`}
                    className="max-h-full max-w-full object-contain transition-transform duration-[1200ms] ease-out group-hover:scale-105"
                    draggable="false"
                  />
                </div>
                <div className="mt-8 flex flex-col items-center">
                  <div className="text-xs tracking-[0.25em] uppercase" style={{ color: "var(--gicp-gold)" }}>{p.sector}</div>
                  <h3 className="mt-3 font-serif text-3xl md:text-4xl">{p.name}</h3>
                  <p className="mt-4 max-w-xs text-sm leading-relaxed" style={{ color: "var(--fg-soft)" }}>{p.note}</p>
                  <ArrowUpRight size={18} className="mt-5 transition-all duration-500 group-hover:translate-x-1 group-hover:-translate-y-1 group-hover:text-[var(--gicp-gold)]" style={{ color: "var(--fg-soft)" }} />
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* PARTNERSHIP CTA */}
      <section className="section relative overflow-hidden">
        <div className="absolute inset-0 -z-10 opacity-[0.18]">
          <img
            src="https://images.unsplash.com/photo-1582213782179-e0d53f98f2ca?w=1800&q=80&auto=format&fit=crop"
            alt=""
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, var(--bg) 0%, transparent 40%, transparent 60%, var(--bg) 100%)" }} />
        </div>
        <div className="container-x grid grid-cols-1 md:grid-cols-12 gap-12 items-end relative">
          <div className="md:col-span-7 js-reveal">
            <span className="overline">— Strategic Partnerships</span>
            <h2 className="font-serif text-4xl md:text-6xl mt-6 leading-[0.95] tracking-tight">
              Boots on the ground.<br/>
              <em className="italic" style={{ color: "var(--gicp-gold)" }}>Variable</em>, not fixed.
            </h2>
          </div>
          <div className="md:col-span-5 js-reveal">
            <div className="surface p-10" style={{ background: "var(--bg-soft)" }}>
              <div className="overline">Engage</div>
              <h3 className="font-serif text-3xl md:text-4xl mt-4 leading-tight">
                Have a mandate, a target, or a thesis worth backing?
              </h3>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link to="/contact" className="btn-gold" data-testid="partnership-cta">
                  Start a conversation <ArrowUpRight size={16} />
                </Link>
                <a href="/gicp.pdf" download="GICP-Profile.pdf" className="btn-ghost" data-testid="partnership-download">
                  Download Company Profile <ArrowUpRight size={16} />
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
